import React, { useState } from 'react';
import { X, Trash2, AlertTriangle } from 'lucide-react';
import { adminService } from '../../services/adminService';
import { toast } from 'react-hot-toast';

interface DeleteUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  user: any;
  userType: 'jobseeker' | 'hr';
}

const DeleteUserModal: React.FC<DeleteUserModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  user,
  userType
}) => {
  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  if (!isOpen || !user) return null;

  const handleDelete = async () => {
    if (confirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }

    setIsDeleting(true);

    try {
      const result = await adminService.deleteUser(user.id, userType);

      if (result.success) {
        toast.success(`${userType === 'jobseeker' ? 'Job seeker' : 'HR user'} deleted successfully!`);
        onSuccess();
        onClose();
        setConfirmText('');
      } else {
        toast.error(result.error || 'Failed to delete user');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete user');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="bg-red-50 border-b border-red-200 px-6 py-4 flex items-center justify-between rounded-t-xl">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-red-900">Delete User</h2>
              <p className="text-sm text-red-700">This action cannot be undone</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-red-400 hover:text-red-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800">
              <strong>Warning:</strong> You are about to permanently delete this {userType === 'jobseeker' ? 'job seeker' : 'HR user'}:
            </p>
            <div className="mt-3 space-y-1 text-sm">
              <p className="font-semibold text-gray-900">{user.name}</p>
              <p className="text-gray-600">{user.email}</p>
            </div>
          </div>

          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-800 font-semibold mb-2">
              This will permanently delete:
            </p>
            <ul className="text-sm text-red-700 space-y-1 list-disc list-inside">
              <li>User profile and account data</li>
              {userType === 'jobseeker' ? (
                <>
                  <li>All test results and submissions</li>
                  <li>Resume data and skills information</li>
                  <li>Gamification progress and achievements</li>
                </>
              ) : (
                <>
                  <li>All created tests and job postings</li>
                  <li>Candidate management data</li>
                  <li>Shortlisted and rejected candidates</li>
                </>
              )}
            </ul>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type <span className="font-bold text-red-600">DELETE</span> to confirm:
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              placeholder="DELETE"
              autoFocus
            />
          </div>

          <p className="text-xs text-gray-500">
            Note: Firebase Authentication account will need to be deleted manually from the Firebase Console or via Admin SDK.
          </p>
        </div>

        {/* Footer Actions */}
        <div className="bg-gray-50 px-6 py-4 flex items-center justify-end space-x-3 rounded-b-xl">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-white transition-colors"
            disabled={isDeleting}
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={isDeleting || confirmText !== 'DELETE'}
            className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {isDeleting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Deleting...</span>
              </>
            ) : (
              <>
                <Trash2 className="w-4 h-4" />
                <span>Delete Permanently</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteUserModal;
